import React, { useMemo } from 'react';
import { Field, FieldProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { QBox, QCheckbox, QFormControl, QInput, QLink, QSelect, QSelectItem, QStack } from '@qualio/ui-components';
import { UserRoleInfoLink } from '../../../constants';
import { UsergroupsList } from '../../../utils/umfe.enums';
import { MedtechRole } from '../../../types/medtechRole.types';

/**
 * Putting this type definition here as any From consuming this component will
 * need to use or extend these types in the Formik initial values
 */
export type UserInfoFormFields = {
  fullName: string;
  email: string;
  role: MedtechRole;
  adminAccess: boolean;
  billingAccess: boolean;
};

export type UserProfileFormFields = {
  jobTitle: string;
  department: string;
  location: string;
};

export const PROFILE_FIELD_MAX_LENGTH = 255;

const ProfileFields: Array<{ name: keyof UserProfileFormFields; labelKey: string; dataCy: string }> = [
  { name: 'jobTitle', labelKey: 'forms.labels.jobTitle', dataCy: 'job-title-input' },
  { name: 'department', labelKey: 'forms.labels.department', dataCy: 'department-input' },
  { name: 'location', labelKey: 'forms.labels.location', dataCy: 'location-input' },
];

type UserInfoProps = {
  inviteIsPending: boolean;
  showProfileFields?: boolean;
};

const UserInfo = ({ inviteIsPending, showProfileFields = false }: UserInfoProps) => {
  const { t } = useTranslation();

  // Convert UsergroupsList into a select box options with translated labels
  const RoleSelectOptions = useMemo(
    () =>
      Object.values(UsergroupsList).map(({ value, labelKey }) => ({
        value,
        label: t(labelKey),
      })),
    [t],
  );
  return (
    <QStack direction={['column']}>
      <QBox pb="2">
        <QFormControl label={t('forms.labels.fullName')} size="sm">
          <Field name="fullName">
            {({ field, meta }: FieldProps) => {
              const { value, onChange, onBlur } = field;
              const { touched, error } = meta;
              const hasError = Boolean(touched && error);

              return (
                <QInput
                  data-cy="name-input"
                  id="fullName"
                  type="name"
                  placeholder={t('forms.labels.fullName')}
                  aria-label="fullName"
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  isInvalid={hasError}
                  isReadOnly={inviteIsPending}
                />
              );
            }}
          </Field>
        </QFormControl>
      </QBox>
      <QBox pb="2">
        <QFormControl label={t('forms.labels.emailAddress')} size="sm">
          <Field name="email">
            {({ field, meta }: FieldProps) => {
              const { value, onChange, onBlur } = field;
              const { touched, error } = meta;
              const hasError = Boolean(touched && error);

              return (
                <QInput
                  data-cy="email-input"
                  id="email"
                  type="email"
                  placeholder={t('forms.labels.emailAddress')}
                  aria-label="email"
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  isInvalid={hasError}
                  isReadOnly={inviteIsPending}
                />
              );
            }}
          </Field>
        </QFormControl>
      </QBox>
      <QBox pb="2">
        <QFormControl data-testid="roleSelectFormControl" label={t('forms.labels.userRole')} size="sm">
          <Field name="role">
            {({ field, form }: FieldProps) => {
              const { value, name: fieldName } = field;
              const { setFieldValue } = form;
              const onChange = ({ value: val }: QSelectItem<string, string>) => {
                setFieldValue(fieldName, val);
              };
              return (
                <QSelect
                  aria-label="role"
                  options={RoleSelectOptions}
                  value={value}
                  onChange={(item) => item && onChange(item)}
                />
              );
            }}
          </Field>
          <QBox fontSize="14px" mt={2}>
            <QLink isExternal href={UserRoleInfoLink}>
              {t('forms.helperText.learnMoreRoles')}
            </QLink>
          </QBox>
        </QFormControl>
      </QBox>
      {showProfileFields &&
        ProfileFields.map(({ name, labelKey, dataCy }) => (
          <QBox pb="2" key={name}>
            <QFormControl label={t(labelKey)} size="sm">
              <Field name={name}>
                {({ field, meta }: FieldProps) => {
                  const { value, onChange, onBlur } = field;
                  const { touched, error } = meta;
                  const hasError = Boolean(touched && error);

                  return (
                    <QInput
                      data-cy={dataCy}
                      id={name}
                      placeholder={t(labelKey)}
                      aria-label={name}
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      isInvalid={hasError}
                      maxLength={PROFILE_FIELD_MAX_LENGTH}
                    />
                  );
                }}
              </Field>
            </QFormControl>
          </QBox>
        ))}
      <QBox pb="2">
        <QFormControl size="sm" helper={t('forms.helperText.adminAccessExtended')}>
          <Field name="adminAccess">
            {({ field, form }: FieldProps) => {
              const { value, name: fieldName } = field;
              const { setFieldValue } = form;

              return (
                <QCheckbox
                  data-cy="admin-access-checkbox"
                  aria-label="admin access"
                  isChecked={value}
                  onChange={(event) => setFieldValue(fieldName, event.target.checked)}
                >
                  {t('forms.labels.adminAccess')}
                </QCheckbox>
              );
            }}
          </Field>
        </QFormControl>
      </QBox>
      <QBox pb="2">
        <QFormControl size="sm">
          <Field name="billingAccess">
            {({ field, form }: FieldProps) => {
              const { value, name: fieldName } = field;
              const { setFieldValue } = form;

              return (
                <QCheckbox
                  data-cy="billing-access-checkbox"
                  aria-label="billing access"
                  isChecked={value}
                  onChange={(event) => setFieldValue(fieldName, event.target.checked)}
                >
                  {t('forms.labels.billingAccess')}
                </QCheckbox>
              );
            }}
          </Field>
        </QFormControl>
      </QBox>
    </QStack>
  );
};

export default UserInfo;
